/**
 * AdminProfileSettings Component
 *
 * Admin user profile and security settings management.
 * Reuses shared authentication components for consistency across user and admin apps.
 */

// 1. External dependencies
import React, { useState, useCallback, useEffect } from 'react';
import { User, Mail, Shield, Calendar } from 'lucide-react';

// 2. Internal absolute imports
import { AdminLayout } from '@/components/shared/layouts';
import {
  Card,
  CardBody,
  Chip,
  Button,
  useDisclosure
} from '@enfomentis/ui';
import {
  PasswordChangeModal,
  DisplayNameModal,
  MFAFactorList,
  MFAEnrollmentModal,
  MFAUnenrollModal,
  ReAuthGate
} from '@/components/shared/auth';
import { useAuth } from '@/hooks/useAuth';
import { mfaService } from '@/services/MFAService';
import type { Factor } from '@/types/mfa';

/**
 * Admin Profile Settings Component
 *
 * Features:
 * - Admin profile information display
 * - Display name editing (shared component)
 * - Password management (shared component)
 * - MFA enrollment/management (shell for Task 4B)
 * - Read-only email display (admins cannot self-change email for security)
 */
export const AdminProfileSettings: React.FC = () => {
  const { user } = useAuth();

  // Display name modal state
  const { isOpen: isDisplayNameOpen, onOpen: onDisplayNameOpen, onOpenChange: onDisplayNameOpenChange } = useDisclosure();

  // Password modal state
  const { isOpen: isPasswordOpen, onOpen: onPasswordOpen, onOpenChange: onPasswordOpenChange } = useDisclosure();

  // MFA enrollment modal state
  const { isOpen: isMFAOpen, onOpen: onMFAOpen, onOpenChange: onMFAOpenChange } = useDisclosure();

  // MFA unenroll modal state
  const { isOpen: isMFAUnenrollOpen, onOpen: onMFAUnenrollOpen, onOpenChange: onMFAUnenrollOpenChange } = useDisclosure();

  // ReAuth gate state
  const { isOpen: isReAuthOpen, onOpen: onReAuthOpen, onOpenChange: onReAuthOpenChange } = useDisclosure();

  // Pending MFA action state
  const [pendingMFAAction, setPendingMFAAction] = useState<'enroll' | 'unenroll' | null>(null);
  const [factorIdToUnenroll, setFactorIdToUnenroll] = useState<string | null>(null);

  // MFA factors state
  const [factors, setFactors] = useState<Factor[]>([]);
  const [factorsLoading, setFactorsLoading] = useState(true);
  const [mfaRefreshKey, setMfaRefreshKey] = useState(0);

  // Load MFA factors
  useEffect(() => {
    const loadFactors = async (): Promise<void> => {
      setFactorsLoading(true);
      const result = await mfaService.listFactors();

      if (result.success) {
        setFactors(result.data);
      } else {
        console.error('Failed to load MFA factors:', result.error);
      }

      setFactorsLoading(false);
    };

    loadFactors();
  }, [mfaRefreshKey]);

  // Helper to check if reauth is valid (client-side check for UX)
  const isReauthValid = useCallback((): boolean => {
    if (!user?.user_metadata?.security?.reauth_at) return false;

    const reauthAt = new Date(user.user_metadata.security.reauth_at).getTime();
    const now = Date.now();
    const elapsed = now - reauthAt;
    const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds

    return elapsed <= tenMinutes;
  }, [user]);

  // Handle MFA enrollment (with reauth check)
  const handleMFAEnroll = useCallback(() => {
    if (isReauthValid()) {
      // Reauth still valid, proceed directly
      onMFAOpen();
    } else {
      // Need reauth
      setPendingMFAAction('enroll');
      onReAuthOpen();
    }
  }, [isReauthValid, onMFAOpen, onReAuthOpen]);

  // Handle MFA manage/unenroll (with reauth check)
  const handleMFAManage = useCallback((factorId: string) => {
    setFactorIdToUnenroll(factorId);

    if (isReauthValid()) {
      // Reauth still valid, proceed directly
      onMFAUnenrollOpen();
    } else {
      // Need reauth
      setPendingMFAAction('unenroll');
      onReAuthOpen();
    }
  }, [isReauthValid, onMFAUnenrollOpen, onReAuthOpen]);

  // Handle successful reauth
  const handleReAuthSuccess = useCallback(() => {
    // Execute pending MFA action
    if (pendingMFAAction === 'enroll') {
      onMFAOpen();
    } else if (pendingMFAAction === 'unenroll') {
      onMFAUnenrollOpen();
    }

    // Clear pending action
    setPendingMFAAction(null);
  }, [pendingMFAAction, onMFAOpen, onMFAUnenrollOpen]);

  // Handle MFA status card refresh after enroll/unenroll
  const handleMFARefresh = useCallback(() => {
    // Force MFAStatusCard to re-mount and fetch fresh data
    setMfaRefreshKey(prev => prev + 1);
  }, []);

  if (!user) {
    return (
      <AdminLayout title="Profile Settings">
        <div className="max-w-4xl space-y-6">
          <Card>
            <CardBody className="p-6 text-center">
              <p className="text-text-secondary">
                Please sign in to view your profile settings.
              </p>
            </CardBody>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  // Format dates
  const createdDate = new Date(user.createdAt).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const lastLoginDate = new Date(user.lastLoginAt).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get admin role from user
  const isAdmin = user.role === 'admin';

  return (
    <AdminLayout title="Profile Settings">
      <div className="max-w-4xl space-y-6">
        {/* Admin Profile Information Card */}
        <Card shadow="sm">
          <CardBody className="p-6">
            <div className="flex items-start gap-4">
              <div className="text-primary">
                <User size={24} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-text-primary">
                    Profile Information
                  </h3>
                  {isAdmin && (
                    <Chip color="primary" variant="flat" size="sm">
                      Admin
                    </Chip>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-text-primary">Display Name</p>
                    <p className="text-text-secondary">{user.displayName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Email</p>
                    <p className="text-text-secondary">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Account Created</p>
                    <p className="text-text-secondary text-sm">{createdDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Last Login</p>
                    <p className="text-text-secondary text-sm">{lastLoginDate}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Display Name Card */}
        <Card shadow="sm">
          <CardBody className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-primary">
                  <User size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">
                    Display Name
                  </h3>
                  <p className="text-text-secondary">
                    {user.displayName}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="bordered"
                  size="sm"
                  onPress={onDisplayNameOpen}
                >
                  Edit Display Name
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Email Card (Read-Only for Admins) */}
        <Card shadow="sm">
          <CardBody className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-primary">
                  <Mail size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">
                    Email Address
                  </h3>
                  <p className="text-text-secondary">
                    {user.email}
                  </p>
                  <p className="text-xs text-text-tertiary mt-1">
                    Admin email addresses cannot be self-changed for security reasons
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Chip
                  color={user.emailVerified ? "success" : "warning"}
                  variant="flat"
                  size="sm"
                >
                  {user.emailVerified ? "Verified" : "Pending"}
                </Chip>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Password Card */}
        <Card shadow="sm">
          <CardBody className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-primary">
                  <Shield size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">
                    Password
                  </h3>
                  <p className="text-text-secondary">
                    ••••••••••••
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="bordered"
                  size="sm"
                  onPress={onPasswordOpen}
                >
                  Change Password
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* MFA Factor List */}
        <MFAFactorList
          factors={factors}
          onAddFactor={handleMFAEnroll}
          onRemoveFactor={handleMFAManage}
          onRefresh={handleMFARefresh}
          isLoading={factorsLoading}
        />

        {/* Account Information Card */}
        <Card shadow="sm">
          <CardBody className="p-6">
            <div className="flex items-start gap-4">
              <div className="text-primary">
                <Calendar size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  Account Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-text-primary">Role</p>
                    <p className="text-text-secondary">{isAdmin ? 'Administrator' : 'User'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">Account Status</p>
                    <Chip color="success" variant="flat" size="sm">
                      Active
                    </Chip>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Display Name Edit Modal */}
        <DisplayNameModal
          isOpen={isDisplayNameOpen}
          onOpenChange={onDisplayNameOpenChange}
          currentDisplayName={user.displayName}
        />

        {/* Password Change Modal */}
        <PasswordChangeModal
          isOpen={isPasswordOpen}
          onOpenChange={onPasswordOpenChange}
        />

        {/* ReAuth Gate */}
        <ReAuthGate
          isOpen={isReAuthOpen}
          onOpenChange={onReAuthOpenChange}
          onReAuth={handleReAuthSuccess}
        />

        {/* MFA Enrollment Modal */}
        <MFAEnrollmentModal
          isOpen={isMFAOpen}
          onOpenChange={onMFAOpenChange}
          onSuccess={handleMFARefresh}
          existingFactorCount={factors.length}
        />

        {/* MFA Unenroll Modal */}
        {factorIdToUnenroll && (() => {
          const factor = factors.find(f => f.id === factorIdToUnenroll);
          return (
            <MFAUnenrollModal
              isOpen={isMFAUnenrollOpen}
              onOpenChange={onMFAUnenrollOpenChange}
              factorId={factorIdToUnenroll}
              {...(factor?.friendly_name && { factorName: factor.friendly_name })}
              remainingFactorsCount={factors.length - 1}
              onSuccess={handleMFARefresh}
            />
          );
        })()}
      </div>
    </AdminLayout>
  );
};
